import { z } from 'zod';
export const channelSchema = z.enum(['whatsapp', 'instagram', 'facebook']);
export const roleSchema = z.enum(['admin', 'agent']);
const isoDate = z.string().datetime();
export const messageStatusSchema = z.enum(['pending', 'sent', 'delivered', 'read', 'error']);
/** Família do arquivo anexado. `sticker` (figurinha) só é recebido, nunca enviado. */
export const attachmentKindSchema = z.enum(['image', 'video', 'audio', 'document', 'sticker']);
/**
 * `uploaded`: enviado por nós, guardado e ainda sem mensagem; `pending`: recebido, o arquivo ainda
 * não foi baixado do provedor; `ready`: o arquivo está no servidor; `failed`: não foi possível baixar.
 */
export const attachmentStatusSchema = z.enum(['uploaded', 'pending', 'ready', 'failed']);
/** O arquivo de uma mensagem (no máximo um por mensagem). O conteúdo vem de `GET /attachments/:id/content`. */
export const attachmentSchema = z.object({
    id: z.string(),
    kind: attachmentKindSchema,
    mimeType: z.string(),
    /** Nome para exibir (nunca usado como caminho no servidor). */
    fileName: z.string().nullable(),
    /** `null` enquanto o arquivo recebido ainda não foi baixado. */
    sizeBytes: z.number().int().nonnegative().nullable(),
    status: attachmentStatusSchema,
    /** Nota de voz do WhatsApp (áudio gravado na hora). */
    voice: z.boolean().default(false),
});
/** O modelo (template) pelo qual uma mensagem de saída foi enviada. */
export const templateRefSchema = z.object({
    name: z.string(),
    language: z.string(),
    category: z.string(),
});
export const messageSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    direction: z.enum(['in', 'out']),
    /** Texto, ou a legenda do anexo; vazio quando a mensagem é só o arquivo. */
    text: z.string(),
    status: messageStatusSchema,
    /** Motivo do erro de envio (ex.: `unknown_delivery`, `rejected`); null se não houve erro. */
    errorCode: z.string().nullable(),
    /** O que o WhatsApp disse ao recusar (código e texto curto), para diagnóstico; `null` se não houve ou não informou. */
    errorDetail: z.string().nullable().optional(),
    authorUserId: z.string().nullable(),
    /** Nome de quem enviou (mensagens de saída feitas por atendentes). */
    authorName: z.string().nullable(),
    createdAt: isoDate,
    /** O arquivo da mensagem, se houver. */
    attachment: attachmentSchema.nullable().default(null),
    /** O modelo de mensagem usado no envio (o texto já é o do modelo montado); `null` nas demais. */
    template: templateRefSchema.nullable().default(null),
    /** Sussurro: só a equipe vê; nunca foi (nem vai) ao cliente. Ausente nas mensagens comuns. */
    internal: z.boolean().optional(),
});
export const contactSchema = z.object({
    id: z.string(),
    name: z.string(),
    phone: z.string().nullable(),
    channel: channelSchema,
});
export const conversationSchema = z.object({
    id: z.string(),
    contactId: z.string(),
    channel: channelSchema,
    status: z.enum(['open', 'closed']),
    assignedTo: z.string().nullable(),
    lastMessageAt: isoDate.nullable(),
    unreadCount: z.number().int().nonnegative(),
});
/** Versão pública do usuário: campos extras (como o hash de senha) são descartados. */
export const userSchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    email: z.string().email(),
    role: roleSchema,
    active: z.boolean(),
});
export const noteSchema = z.object({
    id: z.string(),
    conversationId: z.string(),
    authorUserId: z.string(),
    authorName: z.string(),
    text: z.string().min(1),
    createdAt: isoDate,
    /** Preenchido quando a nota foi editada. */
    updatedAt: isoDate.nullable(),
});
/** Número (linha) de WhatsApp pelo qual uma conversa acontece. */
export const lineRefSchema = z.object({
    id: z.string(),
    /** Nome para exibir (ex.: "Atendimento"); o servidor usa o telefone se o provedor não der um nome. */
    name: z.string(),
    phone: z.string().nullable(),
});
/**
 * Janela para responder com texto livre. Alguns provedores só permitem mensagem livre até um prazo
 * (na Meta, 24 horas) depois da última mensagem DO CLIENTE; passado o prazo, só modelos aprovados.
 */
export const replyWindowSchema = z.object({
    /** Quando a janela fecha; `null` = o cliente ainda não escreveu, então ela está fechada. */
    expiresAt: z.string().datetime().nullable(),
});
/** Conversa como a interface a mostra: contato, responsável e prévia da última mensagem. */
export const conversationViewSchema = z.object({
    id: z.string(),
    status: z.enum(['open', 'closed']),
    unreadCount: z.number().int().nonnegative(),
    lastMessageAt: isoDate.nullable(),
    assignedTo: z.object({ id: z.string(), name: z.string() }).nullable(),
    contact: contactSchema,
    /** Número que recebe/envia esta conversa; null só em conversas anteriores ao suporte a números. */
    line: lineRefSchema.nullable().default(null),
    /** `null` = o provedor não tem regra de janela (sempre dá para responder). */
    replyWindow: replyWindowSchema.nullable().default(null),
    lastMessage: z
        .object({
        text: z.string(),
        direction: z.enum(['in', 'out']),
        status: messageStatusSchema,
        createdAt: isoDate,
        /** Família do anexo da última mensagem (a prévia mostra "Foto", "Documento"… sem texto). */
        attachmentKind: attachmentKindSchema.nullable().default(null),
    })
        .nullable(),
});
/** Resposta da API para quem pergunta: acrescenta o que ESTE usuário pode fazer. */
export const conversationItemSchema = conversationViewSchema.extend({
    /** true se o usuário pode responder, anotar e marcar como lida (admin ou responsável). */
    canWrite: z.boolean(),
});
/** Filtros da lista de conversas ("Minhas", "Não atribuídas", "Todas"). */
/**
 * As abas da lista. `mine`, `unassigned` e `all` mostram só conversas ABERTAS (com busca, também as encerradas);
 * `closed` mostra só as encerradas (docs/conversation-status.md).
 */
export const conversationFilterSchema = z.enum(['mine', 'unassigned', 'all', 'closed']);
/** Resposta de `POST /auth/login` e `GET /auth/me`. */
export const sessionResponseSchema = z.object({
    user: userSchema,
    /** Enviar no cabeçalho `x-csrf-token` em toda requisição que altera dados. */
    csrfToken: z.string().min(1),
});
/** Resposta de `GET /conversations`: mais recentes primeiro; `nextCursor` null = última página. */
export const conversationListResponseSchema = z.object({
    conversations: z.array(conversationItemSchema),
    nextCursor: z.string().nullable(),
});
/** Limites de tamanho que o servidor impõe; o frontend os usa para avisar antes de enviar. */
export const LIMITS = {
    /** Texto de uma mensagem enviada ao cliente. */
    messageText: 4096,
    /** Texto de uma nota interna. */
    noteText: 5000,
    /** Legenda de um anexo (limite do WhatsApp). */
    caption: 1024,
    /** Nome do arquivo anexado. */
    fileName: 255,
};
const MB = 1024 * 1024;
/**
 * O que se pode ENVIAR como anexo (o que a API do WhatsApp aceita), por família. O servidor confere
 * tipo e tamanho (e o conteúdo); a interface usa a mesma lista para avisar antes de enviar.
 */
export const ATTACHMENT_UPLOAD_RULES = {
    image: { label: 'Imagem', mimeTypes: ['image/jpeg', 'image/png'], maxBytes: 5 * MB },
    video: { label: 'Vídeo', mimeTypes: ['video/mp4', 'video/3gpp'], maxBytes: 16 * MB },
    audio: {
        label: 'Áudio',
        mimeTypes: ['audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg'],
        maxBytes: 16 * MB,
    },
    document: {
        label: 'Documento',
        mimeTypes: [
            'application/pdf',
            'text/plain',
            'application/msword',
            'application/vnd.ms-excel',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        ],
        maxBytes: 100 * MB,
    },
};
/** `audio/ogg; codecs=opus` → `audio/ogg` (minúsculo, sem parâmetros). */
export function baseMimeType(mime) {
    return (mime.split(';')[0] ?? '').trim().toLowerCase();
}
/** Família de um tipo que podemos enviar; `null` se o WhatsApp não aceita. */
export function uploadKindOfMime(mime) {
    const base = baseMimeType(mime);
    for (const kind of Object.keys(ATTACHMENT_UPLOAD_RULES)) {
        if (ATTACHMENT_UPLOAD_RULES[kind].mimeTypes.includes(base))
            return kind;
    }
    return null;
}
/** Valor do atributo `accept` do seletor de arquivos (todos os tipos enviáveis). */
export const ATTACHMENT_ACCEPT = Object.keys(ATTACHMENT_UPLOAD_RULES)
    .flatMap((k) => ATTACHMENT_UPLOAD_RULES[k].mimeTypes)
    .join(',');
const MIME_BY_EXTENSION = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    mp4: 'video/mp4',
    '3gp': 'video/3gpp',
    aac: 'audio/aac',
    m4a: 'audio/mp4',
    mp3: 'audio/mpeg',
    amr: 'audio/amr',
    ogg: 'audio/ogg',
    opus: 'audio/ogg',
    pdf: 'application/pdf',
    txt: 'text/plain',
    doc: 'application/msword',
    xls: 'application/vnd.ms-excel',
    ppt: 'application/vnd.ms-powerpoint',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};
/**
 * Tipo do arquivo para enviar: o que o navegador informou, ou, se veio vazio ou genérico, o que a
 * extensão do nome indica. `null` = não dá para saber (o chamador recusa como tipo não permitido).
 */
export function mimeTypeForUpload(declared, fileName) {
    const base = baseMimeType(declared);
    if (base !== '' && base !== 'application/octet-stream')
        return base;
    const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
    return MIME_BY_EXTENSION[ext] ?? null;
}
/** Resposta de `GET /conversations/:id`, `POST /conversations/:id/read` e `POST …/assign`. */
export const conversationResponseSchema = z.object({ conversation: conversationItemSchema });
/**
 * Resposta de `GET /conversations/:id/messages`: as mensagens da página em ordem cronológica
 * (mais antiga primeiro). `nextCursor` aponta para as mais antigas que estas (passe como `before`);
 * null = início da conversa.
 */
export const messageListResponseSchema = z.object({
    messages: z.array(messageSchema),
    nextCursor: z.string().nullable(),
});
/** Resposta de `POST …/messages` (202 nova, 200 repetição idempotente) e de `POST …/retry`. */
export const messageResponseSchema = z.object({ message: messageSchema });
/** Resposta de `POST /conversations/:id/attachments` (201): o arquivo guardado, ainda sem mensagem. */
export const attachmentResponseSchema = z.object({ attachment: attachmentSchema });
/** Resposta de `GET /conversations/:id/notes`: da mais antiga para a mais nova. */
export const noteListResponseSchema = z.object({ notes: z.array(noteSchema) });
/** Resposta de `POST …/notes` (201) e `PATCH …/notes/:id`. */
export const noteResponseSchema = z.object({ note: noteSchema });
/** Resposta de `GET /admin/users`. */
export const userListResponseSchema = z.object({ users: z.array(userSchema) });
/** Resposta de `POST /admin/users` (201) e de `PATCH /admin/users/:id`. */
export const userResponseSchema = z.object({ user: userSchema });
/**
 * Limites dos cadastros da administração. O servidor os impõe (e responde 400 acima deles); a
 * interface os usa para avisar ANTES de enviar. Os testes de contrato do servidor conferem cada
 * valor contra a API real, então um não muda sem o outro.
 */
export const ADMIN_LIMITS = {
    /** Nome de usuário: de 1 (sem contar os espaços das pontas) a este tanto de caracteres. */
    userName: 120,
    userEmail: 254,
    /** Senha de usuário: de `passwordMin` a `passwordMax` caracteres. */
    passwordMin: 10,
    passwordMax: 128,
    chatappEmail: 254,
    chatappAppId: 200,
    chatappLicenseId: 200,
    /** Limites dos campos de credencial do adaptador do ChatApp (a senha é a do serviço, não a de um usuário). */
    chatappPassword: 256,
};
export const syncStateSchema = z.enum(['idle', 'running', 'done', 'error']);
/** Contagens da sincronização inicial (cresce enquanto ela roda). */
export const syncStatsSchema = z.object({
    /** Conversas com ao menos uma mensagem, importadas ou confirmadas. */
    chats: z.number().int().nonnegative(),
    messagesCreated: z.number().int().nonnegative(),
    /** Mensagens que já existiam (execução repetida). */
    messagesKnown: z.number().int().nonnegative(),
    /** Mensagens enviadas por aqui e reencontradas no histórico do ChatApp. */
    reconciled: z.number().int().nonnegative(),
    /** Conversas que falharam (as demais seguem). */
    errors: z.number().int().nonnegative(),
    /** Havia mais conversas do que o teto de importação. */
    truncated: z.boolean(),
});
export const syncStatusSchema = z.object({
    state: syncStateSchema,
    startedAt: isoDate.nullable(),
    finishedAt: isoDate.nullable(),
    stats: syncStatsSchema.nullable(),
    /** Mensagem genérica em português, sem detalhes internos; null fora do estado `error`. */
    error: z.string().nullable(),
});
/** Resposta de `GET /admin/whatsapp/sync` e `POST /admin/whatsapp/sync` (202). */
export const syncStatusResponseSchema = z.object({ status: syncStatusSchema });
/** Eventos da caixa de entrada entregues em tempo real aos clientes. */
export const wsEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('message.created'), message: messageSchema }),
    z.object({
        type: z.literal('message.status'),
        messageId: z.string(),
        conversationId: z.string(),
        status: messageStatusSchema,
        /** Motivo do erro quando status = 'error'; null nos demais casos. */
        errorCode: z.string().nullable(),
        /** O que o WhatsApp disse ao recusar (código e texto curto); `null` nos demais casos. */
        errorDetail: z.string().nullable().optional(),
    }),
    /** O arquivo de uma mensagem recebida terminou de baixar (ou falhou): a bolha se atualiza. */
    z.object({
        type: z.literal('attachment.updated'),
        conversationId: z.string(),
        messageId: z.string(),
        attachment: attachmentSchema,
    }),
    z.object({ type: z.literal('conversation.updated'), conversation: conversationViewSchema }),
    /**
     * A conversa saiu da visão do destinatário (ex.: foi transferida para outra pessoa e a
     * instalação não deixa atendentes verem conversas de colegas). O cliente a remove da lista.
     */
    z.object({ type: z.literal('conversation.removed'), conversationId: z.string() }),
]);
/** Por que o servidor pede que o cliente recarregue tudo pela API HTTP. */
export const resyncReasonSchema = z.enum([
    'gap', // o histórico de eventos já não cobre o ponto em que o cliente parou
    'too_many', // aconteceu coisa demais para reenviar
    'bad_cursor', // o cursor informado não existe (outro banco, valor inventado)
    'data_changed', // mudança grande no servidor (ex.: sincronização inicial)
    'event_log_unavailable', // falha ao registrar eventos: o cliente pode ter perdido algum
    'slow_consumer', // o cliente não acompanhou o ritmo
]);
/** Quadros que o servidor envia pelo WebSocket. */
export const wsServerFrameSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('ready'),
        /** Id do último evento registrado: ponto de partida para quem acabou de carregar os dados. */
        head: z.number().int().nonnegative(),
        serverTime: isoDate,
        user: z.object({ id: z.string(), role: roleSchema }),
        /** Se atendentes veem conversas de colegas (o cliente ajusta a interface). */
        agentsSeeOthers: z.boolean(),
    }),
    z.object({
        type: z.literal('event'),
        /** Id crescente do evento; guarde o último para retomar depois de uma queda. */
        id: z.number().int().positive(),
        /** Momento do registro (microssegundos); também serve para retomar (`since`). */
        at: isoDate,
        event: wsEventSchema,
    }),
    z.object({
        type: z.literal('resync'),
        reason: resyncReasonSchema,
        /** Depois de recarregar pela API, continue a partir deste id. */
        head: z.number().int().nonnegative(),
    }),
    z.object({ type: z.literal('pong') }),
]);
/** Quadros que o cliente pode enviar (só o batimento; o resto é ignorado). */
export const wsClientFrameSchema = z.object({ type: z.literal('ping') });
/** Códigos de fechamento do WebSocket com significado próprio. */
export const WS_CLOSE = {
    /** Sessão encerrada (logout, expirou, usuário desativado): não reconectar sem novo login. */
    sessionEnded: 4401,
    /** Excesso de conexões do mesmo usuário: esta foi substituída por uma mais nova. */
    replaced: 4408,
};
// ------------------------------------------------------------------ WhatsApp (conexão e números)
//
// O produto não nomeia o serviço de mensagens por trás: a administração fala em "conexão" e em
// "números". O que a conexão pede (e-mail, token…) depende do provedor ativo no servidor, e ele
// mesmo declara os campos (`fields`), então a mesma tela serve a qualquer provedor.
/** Um campo que o provedor pede ao administrador. */
export const credentialFieldSchema = z.object({
    /** Chave técnica enviada ao servidor (ex.: `accessToken`). */
    key: z.string(),
    /** Rótulo em português. */
    label: z.string(),
    hint: z.string().nullable(),
    /** Campo secreto: mostrado mascarado, guardado criptografado e NUNCA devolvido pela API. */
    secret: z.boolean(),
    required: z.boolean(),
    maxLength: z.number().int().positive(),
});
/** Um número de WhatsApp da conexão (descoberto pelo provedor). */
export const lineSchema = lineRefSchema.extend({
    channel: channelSchema,
    /** Número desligado: não recebe nem envia; as conversas dele continuam legíveis. */
    enabled: z.boolean(),
    /** Horas de janela para responder com texto livre (24 na Meta); `null` = sem regra. */
    replyWindowHours: z.number().int().positive().nullable().default(null),
});
/** `GET /lines` (qualquer usuário): só os números ligados. */
export const linesResponseSchema = z.object({ lines: z.array(lineSchema) });
export const connectionStateSchema = z.object({
    /** O que o provedor pede ao administrador (vazio = nada a preencher). */
    fields: z.array(credentialFieldSchema),
    /** Valores atuais dos campos que NÃO são secretos (`key` → valor). */
    values: z.record(z.string(), z.string()),
    /** Chaves dos campos secretos que já estão guardados (o valor nunca volta). */
    secretsSet: z.array(z.string()),
    /** Conexão pronta para uso (credenciais salvas, ou nada a pedir). */
    connected: z.boolean(),
    /** Todos os números da conexão, ligados ou não. */
    lines: z.array(lineSchema),
    /** O provedor tem importação de histórico (as rotas `/admin/whatsapp/sync` existem). */
    canSync: z.boolean(),
    /**
     * Caminho (relativo à API, ex.: `/webhooks/meta`) do endereço que o provedor chama para ENTREGAR as
     * mensagens recebidas; `null` = o provedor não usa webhook. A tela mostra o endereço completo para
     * o administrador cadastrar no painel do provedor.
     */
    webhookPath: z.string().nullable().default(null),
    /**
     * O provedor deixa inscrever a conta no app por um botão (`POST /admin/whatsapp/subscribe`): na Meta, sem
     * essa inscrição a conta não manda as mensagens. Só vale com a conexão pronta.
     */
    canSubscribe: z.boolean().default(false),
    /**
     * O provedor deixa ver a situação de cada número e concluir o registro dele por um botão
     * (`GET /admin/whatsapp/lines/:id/status`, `POST …/register`): na Meta, um número "Pendente" precisa ser registrado
     * (com um PIN de 6 dígitos) antes de enviar e receber. Só vale com a conexão pronta.
     */
    canRegisterLines: z.boolean().default(false),
    /** Ambiente de demonstração: o painel de simulação está disponível. */
    simulation: z.boolean(),
});
/** `GET /admin/whatsapp`, `PUT /admin/whatsapp/connection`, `POST /admin/whatsapp/lines/refresh`. */
export const connectionResponseSchema = z.object({ connection: connectionStateSchema });
/** `POST /admin/whatsapp/subscribe`: a conta foi inscrita no app. */
export const subscribeResponseSchema = z.object({ subscribed: z.literal(true) });
/** Corpo de `PUT /admin/whatsapp/connection`: campo secreto ausente ou vazio MANTÉM o guardado. */
export const saveConnectionBodySchema = z.object({ values: z.record(z.string(), z.string()) });
/** `PATCH /admin/whatsapp/lines/:id`. */
export const patchLineBodySchema = z.object({ enabled: z.boolean() }).strict();
export const lineResponseSchema = z.object({ line: lineSchema });
/**
 * `GET /admin/whatsapp/lines/:id/status`: a situação do número no provedor. Cada campo é um código do provedor em
 * maiúsculas (ex.: `CONNECTED`, `PENDING`) ou `null` se ele não informou; a tela os traduz em palavras.
 */
export const lineStatusResponseSchema = z.object({
    /** Estado do número: `CONNECTED`, `PENDING`, `DISCONNECTED`, `FLAGGED`, `RESTRICTED`, `BANNED`… */
    status: z.string().nullable(),
    /** Aprovação do nome de exibição: `APPROVED`, `AVAILABLE_WITHOUT_REVIEW`, `PENDING_REVIEW`, `DECLINED`… */
    nameStatus: z.string().nullable(),
    /** Verificação da posse do número (código por SMS ou ligação): `VERIFIED`, `NOT_VERIFIED`, `EXPIRED`. */
    codeVerification: z.string().nullable(),
    /** Qualidade do número: `GREEN`, `YELLOW`, `RED`, `UNKNOWN`. */
    quality: z.string().nullable(),
});
/** Tamanho do PIN da verificação em duas etapas do número (Meta: exatamente 6 dígitos). */
export const LINE_PIN_LENGTH = 6;
/**
 * Corpo de `POST /admin/whatsapp/lines/:id/register`: o PIN que a pessoa escolhe (ou já tinha) para o número. É um
 * segredo digitado na tela: o servidor o repassa ao provedor e não o guarda nem o registra.
 */
export const registerLineBodySchema = z
    .object({ pin: z.string().regex(/^\d{6}$/, 'O PIN tem 6 dígitos') })
    .strict();
export const registerLineResponseSchema = z.object({ registered: z.literal(true) });
/** Simulação (só com o provedor simulado; fora dele as rotas respondem 404). */
export const simulateInboundBodySchema = z.object({
    lineId: z.string(),
    contactName: z.string().trim().min(1).max(120).nullish(),
    phone: z.string().trim().min(5).max(30),
    text: z.string().trim().min(1).max(20_000),
});
export const simulateInboundResponseSchema = z.object({
    conversationId: z.string(),
    messageId: z.string(),
});
/** `POST /admin/whatsapp/simulate/seed`: quantas conversas de exemplo foram criadas. */
export const simulateSeedResponseSchema = z.object({
    conversations: z.number().int().nonnegative(),
});
// ------------------------------------------------------------------ Modelos de mensagem (templates)
//
// Fora da janela de 24 h só vale um modelo aprovado pelo provedor (docs/templates.md). Quem os cria e
// aprova é o provedor; o app lista os aprovados, monta o texto e envia.
export const templateCategorySchema = z.enum(['utility', 'marketing', 'authentication', 'other']);
/** Um campo a preencher (`{{1}}` ou `{{nome}}`) no cabeçalho ou no corpo do modelo. */
export const templateVariableSchema = z.object({
    /** `header:<nome>` ou `body:<nome>`: a chave do valor ao enviar. */
    key: z.string(),
    component: z.enum(['header', 'body']),
    /** O que está entre chaves: `1`, `2`… (posicional) ou um nome. */
    name: z.string(),
    /** Exemplo cadastrado no provedor (ajuda a preencher). */
    example: z.string().nullable(),
});
export const messageTemplateSchema = z.object({
    name: z.string(),
    language: z.string(),
    category: templateCategorySchema,
    header: z.object({ text: z.string() }).nullable(),
    body: z.string(),
    footer: z.string().nullable(),
    /** Só o rótulo dos botões (eles não têm valores a preencher nos modelos suportados). */
    buttons: z.array(z.object({ type: z.string(), text: z.string() })),
    variables: z.array(templateVariableSchema),
    /** `false`: o modelo usa algo que o app ainda não envia (ver `unsupportedReason`). */
    supported: z.boolean(),
    unsupportedReason: z.string().nullable(),
});
/** `GET /message-templates`: `supported: false` = o provedor não tem modelos (lista vazia). */
export const messageTemplatesResponseSchema = z.object({
    supported: z.boolean(),
    templates: z.array(messageTemplateSchema),
});
export const TEMPLATE_LIMITS = {
    /** Um valor de variável do corpo. */
    bodyValue: 1024,
    /** Um valor de variável do cabeçalho. */
    headerValue: 60,
    /** Variáveis por modelo. */
    variables: 20,
    nameLength: 512,
};
/** O modelo escolhido para enviar, com os valores (por `key` da variável). */
export const sendTemplateSchema = z
    .object({
    name: z.string().trim().min(1).max(TEMPLATE_LIMITS.nameLength),
    language: z.string().trim().min(2).max(20),
    values: z.record(z.string(), z.string()),
})
    .strict();
/** Corpo de `POST /conversations`: começa uma conversa com um modelo. */
export const newConversationBodySchema = z
    .object({
    lineId: z.string().uuid(),
    phone: z.string().trim().min(1).max(40),
    name: z.string().trim().max(120).optional(),
    template: sendTemplateSchema,
    clientId: z.string().trim().min(1).max(100).optional(),
})
    .strict();
export const newConversationResponseSchema = z.object({
    conversationId: z.string(),
    /** `true`: a conversa foi criada agora; `false`: já existia e o modelo foi enviado nela. */
    created: z.boolean(),
    message: messageSchema,
});
/**
 * Deixa um valor no formato que o provedor aceita: quebras de linha e tabulações viram espaço, 4 ou mais
 * espaços seguidos viram um, as pontas são aparadas. (A Meta recusa o resto.)
 */
export function cleanTemplateValue(raw) {
    return raw
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/ {4,}/g, ' ')
        .trim();
}
/**
 * Os valores do modelo são posicionais (`{{1}}`) ou nomeados (`{{nome}}`)? Decide o formato do parâmetro no
 * envio (os nomeados levam o nome).
 */
export const usesNamedVariables = (variables) => variables.some((v) => !/^\d+$/.test(v.name));
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
/** Os nomes (`1`, `nome`) que aparecem entre chaves no texto, na ordem e sem repetir. */
export function placeholderNames(text) {
    const names = [];
    for (const m of text.matchAll(PLACEHOLDER)) {
        if (!names.includes(m[1]))
            names.push(m[1]);
    }
    return names;
}
/**
 * O texto do modelo com os valores no lugar (cabeçalho, corpo e rodapé, separados por linha em branco).
 * Variável sem valor fica como `{{nome}}`, para a prévia mostrar o que falta. Usado igual pelo servidor
 * (que grava o texto) e pela interface (que mostra a prévia).
 */
export function renderTemplateText(template, values) {
    const fill = (text, component) => text.replace(PLACEHOLDER, (whole, name) => {
        const value = values[`${component}:${name}`];
        return value !== undefined && value.trim() !== '' ? cleanTemplateValue(value) : whole;
    });
    return [
        template.header ? fill(template.header.text, 'header') : null,
        fill(template.body, 'body'),
        template.footer,
    ]
        .filter((part) => part !== null && part !== '')
        .join('\n\n');
}
// ------------------------------------------------------------------ Bitrix24 (Leads)
//
// Quando o cliente escreve, o app confere (ou cria) o Lead dele no Bitrix24 (docs/bitrix.md).
export const BITRIX_LIMITS = {
    /** Dias de atividade de um Lead (0 = sem limite). */
    activeDaysMax: 3650,
    idLength: 64,
};
export const bitrixStatusSchema = z.enum([
    'not_configured',
    'awaiting_install',
    'connected',
    'error',
]);
/** Um id de etapa, origem ou usuário do Bitrix (`NEW`, `UC_ABC123`, `C1:NEW`, `7`…). */
const bitrixId = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_:|.-]+$/, 'Valor inválido')
    .max(BITRIX_LIMITS.idLength);
export const bitrixSettingsSchema = z.object({
    /** Desligada: nada é conferido nem criado no Bitrix. */
    enabled: z.boolean(),
    /** Usuário do Bitrix que recebe os Leads novos; sem ele o app só vincula os que já existem. */
    assignedUserId: bitrixId.nullable(),
    /** Etapa inicial dos Leads novos; `null` = a primeira do funil. */
    stageId: bitrixId.nullable(),
    sourceId: bitrixId.nullable(),
    /** Só vale um Lead alterado nos últimos N dias; 0 = sem limite (vale só o status do Bitrix). */
    activeDays: z.number().int().min(0).max(BITRIX_LIMITS.activeDaysMax),
});
/** A conexão para a tela de administração. NUNCA traz o segredo do aplicativo nem tokens. */
export const bitrixConnectionSchema = z.object({
    status: bitrixStatusSchema,
    domain: z.string().nullable(),
    clientId: z.string().nullable(),
    /** O segredo do aplicativo está guardado (a tela não o mostra). */
    hasSecret: z.boolean(),
    /** Caminhos (sem o `/api`) que o Bitrix chama; a tela monta o endereço completo. `null` até haver conexão. */
    installPath: z.string().nullable(),
    handlerPath: z.string().nullable(),
    lastError: z.string().nullable(),
    settings: bitrixSettingsSchema,
    /** Conferências na fila e, destas, as que já falharam e esperam para tentar de novo. */
    pending: z.number().int().nonnegative(),
    retrying: z.number().int().nonnegative(),
});
export const bitrixConnectionResponseSchema = z.object({ connection: bitrixConnectionSchema });
/**
 * O Canal Aberto do Bitrix (bate-papo no card; docs/bitrix-openlines.md). `ready`: o conector está registrado e os
 * eventos assinados no portal. `hasApplicationToken`: o Bitrix já entregou o token que confere os eventos (um
 * aplicativo instalado antes desta versão precisa ser salvo de novo no Bitrix).
 */
export const bitrixOpenlinesSchema = z.object({
    enabled: z.boolean(),
    ready: z.boolean(),
    hasApplicationToken: z.boolean(),
    /** Com responsável no CladvApp, o bot da linha do Bitrix sai da conversa (passa para um atendente da linha). */
    handoffOnAssign: z.boolean(),
    /**
     * O atalho "Modelos de mensagem" no painel "Aplicativos" do chat do Bitrix: registrado no portal, ou o código de por que
     * não (`scope` = faltam as permissões `placement` e `im` no aplicativo). Nulo/ausente em servidores antigos.
     */
    widget: z
        .object({
        registered: z.boolean(),
        error: z.string().nullable(),
        errorDetail: z.string().nullable(),
    })
        .default({ registered: false, error: null, errorDetail: null }),
    /** Código do último problema ao configurar (`scope` = faltam permissões no aplicativo), ou nulo. */
    lastError: z.string().nullable(),
    /** Em qual passo e com qual resposta do Bitrix falhou (sem dados sensíveis), para quem for diagnosticar. */
    lastErrorDetail: z.string().nullable().default(null),
    lines: z.array(z.object({
        id: z.string(),
        name: z.string(),
        /** Número da linha aberta no Bitrix; nulo = ainda não criada. */
        bitrixLineId: z.number().int().nullable(),
        active: z.boolean(),
    })),
    pending: z.number().int().nonnegative(),
    retrying: z.number().int().nonnegative(),
});
export const bitrixOpenlinesResponseSchema = z.object({ openlines: bitrixOpenlinesSchema });
/** Corpo de `PUT /admin/bitrix/openlines`: o que mudar (ao menos um dos dois). */
export const setBitrixOpenlinesBodySchema = z
    .object({ enabled: z.boolean().optional(), handoffOnAssign: z.boolean().optional() })
    .strict()
    .refine((b) => b.enabled !== undefined || b.handoffOnAssign !== undefined, {
    message: 'Informe o que mudar',
});
/**
 * `PUT /admin/bitrix/connection`. Só o domínio é obrigatório: o ID e a chave do aplicativo só aparecem no
 * Bitrix depois de cadastrar o aplicativo (que já pede os endereços gerados a partir do domínio). Ausentes,
 * o que está guardado é mantido.
 */
export const saveBitrixConnectionBodySchema = z
    .object({
    domain: z.string().trim().min(1).max(253),
    clientId: z.string().trim().min(1).max(200).optional(),
    clientSecret: z.string().trim().min(1).max(500).optional(),
})
    .strict();
/** `PUT /admin/bitrix/settings`. */
export const saveBitrixSettingsBodySchema = bitrixSettingsSchema.strict();
/** `GET /admin/bitrix/options`: o que o Bitrix tem para escolher nos campos da tela. */
export const bitrixOptionsResponseSchema = z.object({
    users: z.array(z.object({ id: z.string(), name: z.string(), email: z.string().nullable() })),
    stages: z.array(z.object({ id: z.string(), name: z.string() })),
    sources: z.array(z.object({ id: z.string(), name: z.string() })),
});
/**
 * Quem é quem no Bitrix (Administração → Usuários): o usuário do Bitrix que corresponde a um usuário do CladvApp.
 * Sem vínculo, vale o usuário do Bitrix com o mesmo e-mail.
 */
export const bitrixUserLinkSchema = z.object({ userId: z.string(), bitrixUserId: z.string() });
export const bitrixUserLinksResponseSchema = z.object({ links: z.array(bitrixUserLinkSchema) });
/** `PUT /admin/bitrix/user-links/:userId`: `null` desfaz o vínculo (volta ao e-mail). */
export const setBitrixUserLinkBodySchema = z
    .object({
    bitrixUserId: z
        .string()
        .regex(/^[0-9]{1,12}$/)
        .nullable(),
})
    .strict();
export const setBitrixUserLinkResponseSchema = z.object({ link: bitrixUserLinkSchema.nullable() });
/** `POST /admin/bitrix/test`. */
export const bitrixTestResponseSchema = z.object({ portal: z.string() });
/**
 * O Lead da conversa (`GET /conversations/:id/bitrix`).
 *  - off: integração desligada ou sem conexão (a tela não mostra nada);
 *  - pending: conferência na fila, ainda sem resultado;
 *  - linked: há um Lead (`lead`); `rechecking` = há uma nova conferência na fila;
 *  - none: não há Lead e nenhum foi criado (`errorCode` diz por quê);
 *  - error: o Bitrix recusou ou não respondeu (`errorCode`).
 */
export const conversationBitrixSchema = z.object({
    state: z.enum(['off', 'pending', 'linked', 'none', 'error']),
    lead: z
        .object({
        id: z.string(),
        title: z.string().nullable(),
        stageId: z.string().nullable(),
        /** Nome da etapa, se o app conseguiu perguntar ao Bitrix. */
        stageName: z.string().nullable(),
        url: z.string(),
        /** O app criou este Lead (em vez de achá-lo já existente). */
        createdByApp: z.boolean(),
    })
        .nullable(),
    errorCode: z.string().nullable(),
    rechecking: z.boolean(),
    checkedAt: isoDate.nullable(),
});
export const conversationBitrixResponseSchema = z.object({ bitrix: conversationBitrixSchema });
// ------------------------------------------------------------------ Respostas rápidas
//
// Textos prontos que a equipe insere no campo de mensagem (docs/quick-replies.md).
export const QUICK_REPLY_LIMITS = {
    /** Tamanho do atalho (sem a barra). */
    shortcutMax: 32,
    /** O mesmo limite de uma mensagem. */
    bodyMax: LIMITS.messageText,
    /** Quantas respostas podem existir. */
    maxCount: 500,
};
/** Letras minúsculas sem acento, números, `_` e `-`; começa por letra ou número. */
export const QUICK_REPLY_SHORTCUT_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;
/** O atalho como a pessoa digita (com ou sem `/`, em qualquer caixa) → como é guardado. */
export const quickReplyShortcutSchema = z
    .string()
    .transform((v) => v.trim().replace(/^\/+/, '').toLowerCase())
    .pipe(z
    .string()
    .min(1, 'Informe o atalho')
    .max(QUICK_REPLY_LIMITS.shortcutMax)
    .regex(QUICK_REPLY_SHORTCUT_PATTERN, 'Use só letras sem acento, números, _ e -'));
export const quickReplyBodySchema = z
    .string()
    .max(QUICK_REPLY_LIMITS.bodyMax)
    .transform((v) => v.trim())
    .pipe(z.string().min(1, 'Informe o texto'));
export const quickReplySchema = z.object({
    id: z.string(),
    shortcut: z.string(),
    body: z.string(),
    updatedAt: isoDate,
});
/** Resposta de `GET /quick-replies`. */
export const quickRepliesResponseSchema = z.object({ quickReplies: z.array(quickReplySchema) });
/** Resposta de `POST /admin/quick-replies` (201) e `PUT /admin/quick-replies/:id`. */
export const quickReplyResponseSchema = z.object({ quickReply: quickReplySchema });
/** `POST /admin/quick-replies` e `PUT /admin/quick-replies/:id`. */
export const saveQuickReplyBodySchema = z
    .object({ shortcut: quickReplyShortcutSchema, body: quickReplyBodySchema })
    .strict();
//# sourceMappingURL=index.js.map